import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TokenInfo } from './types';
import { AssetActor, AssetActorManager } from './services/assetActor';
import { useAssetActor } from './hooks/useAssetActor';
import { apiService } from './services/api';
import { AccountLink } from './components/AccountLink';
import AssetSelector from './components/AssetSelector';
import Balance from './components/Balance';
import OrderBook from './components/OrderBook';
import TradeHistory from './components/TradeHistory';
import './App.css';

function App() {
    const [selectedAsset, setSelectedAsset] = useState<TokenInfo | null>(null);
    const [availableAssets, setAvailableAssets] = useState<TokenInfo[]>([]);
    const [isDarkTheme, setIsDarkTheme] = useState(true);
    const [connectionError, setConnectionError] = useState<string | null>(null);
    const [currentActor, setCurrentActor] = useState<AssetActor | null>(null);
    const [showFailedTrades, setShowFailedTrades] = useState<boolean>(false);

    const assetRefreshInterval = useRef<NodeJS.Timeout | null>(null);
    const actorManager = useRef<AssetActorManager>(new AssetActorManager());

    // Use the asset actor hook to get data for the current asset
    const { orderBook, trades, balance, tokenStats, updateCounter, isActive, isConnected } = useAssetActor(currentActor);

    // Function to load assets from backend
    const loadAssets = useCallback(async () => {
        try {
            const assets = await apiService.getAssets();
            setAvailableAssets(prevAssets => {
                // Only update if assets have actually changed
                if (JSON.stringify(prevAssets) !== JSON.stringify(assets)) {
                    return assets;
                }
                return prevAssets;
            });

            // Set initial selected asset if none selected
            setSelectedAsset(prev => {
                if (!prev && assets.length > 0) {
		    // Default to SOL when available, otherwise fall back to the first asset
		    return assets.find(a => a.symbol.toUpperCase() === 'SOL') ?? assets[0];
	        } else {
		    return prev;
		}
            });
        } catch (error) {
            console.error('Failed to load assets:', error);
            setConnectionError(error instanceof Error ? error.message : 'Failed to load assets');
        }
    }, []);

    // Apply theme to document
    useEffect(() => {
        document.body.className = isDarkTheme ? 'dark-theme' : 'light-theme';
    }, [isDarkTheme]);

    // Initialize app and load assets
    useEffect(() => {
        const initializeApp = async () => {
            try {
                // Load initial assets
                await loadAssets();

                // Set up periodic asset refresh every 5 seconds
                assetRefreshInterval.current = setInterval(loadAssets, 5000);

            } catch (error) {
                console.error('Failed to initialize app:', error);
                setConnectionError(error instanceof Error ? error.message : 'Failed to connect to backend');
            }
        };

        initializeApp();

        // Cleanup on unmount
        return () => {
            if (assetRefreshInterval.current) {
                clearInterval(assetRefreshInterval.current);
		assetRefreshInterval.current = null;
            }
            // Cleanup all actors - capture the ref value to avoid ESLint warning
            const currentActorManager = actorManager.current;
            currentActorManager.cleanup();
        };
    }, [loadAssets]);

    // Handle asset changes - Simplified since actors maintain persistent state
    useEffect(() => {
        if (selectedAsset) {
            const switchAsset = async () => {
                try {
                    console.log(`Switching to asset: ${selectedAsset.symbol}`);

                    // STEP 1: Clear the current actor from React state to prevent hook from using stale data
                    setCurrentActor(null);

                    // STEP 2: Switch to the new actor (handles activation/deactivation)
                    // The actor maintains its own persistent state and WebSocket connection
                    const newActor = await actorManager.current.setActiveActor(selectedAsset);

                    // STEP 3: Set the actor in React state - hook will subscribe to the active actor
                    setCurrentActor(newActor);

                    setConnectionError(null);
                    console.log(`Successfully switched to asset: ${selectedAsset.symbol}`);
                } catch (error) {
                    console.error('Failed to switch asset:', error);
                    setConnectionError(error instanceof Error ? error.message : 'Failed to switch asset');
                }
            };

            switchAsset();
        } else {
            // No asset selected, clear current actor
            setCurrentActor(null);
        }
    }, [selectedAsset]);

    const handleAssetChange = useCallback((asset: TokenInfo) => {
        setSelectedAsset(asset);
    }, []);

    const toggleTheme = useCallback(() => {
        setIsDarkTheme(prev => !prev);
    }, []);

    const handleReconnect = useCallback(async () => {
        if (selectedAsset && currentActor) {
            try {
                setConnectionError(null);
                // Reactivate the current actor to reconnect WebSocket
                await currentActor.deactivate();
                await currentActor.activate();
            } catch (error) {
                setConnectionError(error instanceof Error ? error.message : 'Failed to reconnect');
            }
        }
    }, [selectedAsset, currentActor]);

    if (availableAssets.length === 0) {
        return (
            <div className="app">
                <div className="loading-screen">
                    <h2>Loading Aquifer DEX...</h2>
                    {connectionError && (
                        <div className="error-message">
                            <p>Error: {connectionError}</p>
                            <button onClick={handleReconnect}>Retry Connection</button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="app">
            <header className="app-header">
                <div className="header-content">
                    <h1>Aquifer DEX</h1>
                    <div className="header-subtitle">
                        Real-time Order Book & Trade Stream
			<div className="header-subtitle-links">
	                    <div className="label-value">
	                        <span className="label">Program:</span>
	                        <AccountLink account="AQU1FRd7papthgdrwPTTq5JacJh8YtwEXaBfKU3bTz45" />
	                    </div>
	                    <div className="label-value">
	                        <span className="label">DEX:</span>
	                        <AccountLink account="CNC5TaeNQEoSPfQKZ7GgfM4R8WYAJRKRSHFCHkf2H7ko" />
	                    </div>
			</div>
                    </div>
                </div>
                <div className="header-controls">
                    <div className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                        {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
                    </div>
                    <button
                        className="theme-toggle"
                        onClick={toggleTheme}
                        title={`Switch to ${isDarkTheme ? 'light' : 'dark'} theme`}
                    >
                        {isDarkTheme ? '☀️' : '🌙'}
                    </button>
                    {!isConnected && (
                        <button
                            className="reconnect-button"
                            onClick={handleReconnect}
                        >
                            🔄 Reconnect
                        </button>
                    )}
                </div>
            </header>

            <div className="app-content">
                <aside className="sidebar">
                    <AssetSelector
                        availableAssets={availableAssets}
                        selectedAsset={selectedAsset!}
                        onAssetChange={handleAssetChange}
                    />
                </aside>

                <main className="main-content">
                    {selectedAsset && (
                        <div className="asset-view">
			    <div className="balance-section">
			        <Balance balance={balance} tokenStats={tokenStats} asset={selectedAsset} />
			    </div>
                            <div className="order-book-section">
                                <OrderBook orderBook={orderBook} asset={selectedAsset} />
                            </div>
                            <div className="trades-section">
                                <TradeHistory
                                    trades={trades}
                                    asset={selectedAsset}
				    showFailedTrades={showFailedTrades}
				    setShowFailedTrades={setShowFailedTrades}
                                />
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {isConnected && (
                <div className="streaming-indicator">
                    <div className="streaming-dot"></div>
                    <span>Live data streaming for {selectedAsset?.symbol}</span>
                </div>
            )}
        </div>
    );
}

export default App; 

